// =============================================================================
// typography.ts - Font definitions with responsive desktop/mobile variants
// =============================================================================
// Separates typography from theme.ts for clarity and reusability.
// Each variant has mobile (default) and desktop values.
// theme.ts will use mediaQueries to apply the responsive switch.
// =============================================================================

import type { CSSProperties } from "react";

// ---------------------------------------------------------------------------
// Font families
// Uses CSS variable for Next.js font optimization compatibility.
// EGDE-671: Noto Sans Hebrew replaced Almoni DL AAA as the site font. The
// variable is published by the localFont() call in app/[locale]/layout.tsx.
// ---------------------------------------------------------------------------
export const fontFamily = {
  primary: "var(--noto-sans-hebrew)",
} as const;

// ---------------------------------------------------------------------------
// Font weights - named weights from Figma typography tokens
// ---------------------------------------------------------------------------
// EGDE-738: body copy across the site sets at `medium`, not `regular` — the
// variable face carries every instance from 100 to 900, so 500 is a real
// weight rather than a synthetic one. `regular` stays 400 because that is what
// 400 is; it is the scale, not the choice. Reach for `medium` for body text.
export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

// ---------------------------------------------------------------------------
// Typography variant type - mobile + desktop responsive pair
// ---------------------------------------------------------------------------
export interface TypographyVariant {
  mobile: {
    fontSize: number;
    lineHeight: number;
    fontWeight: number;
    letterSpacing?: number;
  };
  desktop: {
    fontSize: number;
    lineHeight: number;
    fontWeight: number;
    letterSpacing?: number;
  };
}

// ---------------------------------------------------------------------------
// Typography variants - all text styles from Figma
// Each has mobile (base) and desktop responsive values.
// Values are NUMBERS in pixels (theme.ts converts to "px" strings).
// ---------------------------------------------------------------------------
export const typographyVariants = {
  h1: {
    mobile: { fontSize: 40, lineHeight: 44, fontWeight: 700 },
    desktop: { fontSize: 56, lineHeight: 64, fontWeight: 700 },
  },
  h2: {
    mobile: { fontSize: 32, lineHeight: 38, fontWeight: 700 },
    desktop: { fontSize: 48, lineHeight: 48, fontWeight: 700 },
  },
  h3: {
    mobile: { fontSize: 24, lineHeight: 28, fontWeight: 700 },
    desktop: { fontSize: 32, lineHeight: 40, fontWeight: 700 },
  },
  subtitle1: {
    mobile: { fontSize: 18, lineHeight: 22, fontWeight: 600 },
    desktop: { fontSize: 20, lineHeight: 28, fontWeight: 600 },
  },
  body1: {
    mobile: { fontSize: 18, lineHeight: 22, fontWeight: 500 },
    desktop: { fontSize: 20, lineHeight: 28, fontWeight: 500 },
  },
  body2: {
    mobile: { fontSize: 16, lineHeight: 20, fontWeight: 500 },
    desktop: { fontSize: 18, lineHeight: 26, fontWeight: 500 },
  },
  body3: {
    mobile: { fontSize: 14, lineHeight: 18, fontWeight: 500 },
    desktop: { fontSize: 16, lineHeight: 24, fontWeight: 500 },
  },
  body4: {
    mobile: { fontSize: 12, lineHeight: 16, fontWeight: 500 },
    desktop: { fontSize: 14, lineHeight: 18, fontWeight: 500 },
  },
  number: {
    mobile: { fontSize: 64, lineHeight: 83, fontWeight: 500 },
    desktop: { fontSize: 80, lineHeight: 104, fontWeight: 500 },
  },
} as const satisfies Record<string, TypographyVariant>;

// ---------------------------------------------------------------------------
// Helper: Convert a TypographyVariant to a CSS properties object (mobile base)
// Used by theme.ts to build MUI typography config.
// ---------------------------------------------------------------------------
export function variantToCSS(variant: TypographyVariant): CSSProperties {
  return {
    fontSize: `${variant.mobile.fontSize}px`,
    lineHeight: `${variant.mobile.lineHeight}px`,
    fontWeight: variant.mobile.fontWeight,
    ...(variant.mobile.letterSpacing != null && {
      letterSpacing: `${variant.mobile.letterSpacing}px`,
    }),
  };
}

// ---------------------------------------------------------------------------
// Helper: Get desktop responsive overrides for a variant
// Used by theme.ts to add media query responsive values.
// ---------------------------------------------------------------------------
export function variantDesktopCSS(variant: TypographyVariant): CSSProperties {
  return {
    fontSize: `${variant.desktop.fontSize}px`,
    lineHeight: `${variant.desktop.lineHeight}px`,
    fontWeight: variant.desktop.fontWeight,
    ...(variant.desktop.letterSpacing != null && {
      letterSpacing: `${variant.desktop.letterSpacing}px`,
    }),
  };
}

// Consolidated export
export const typography = {
  fontFamily,
  fontWeights,
  variants: typographyVariants,
  variantToCSS,
  variantDesktopCSS,
} as const;

export type Typography = typeof typography;
